import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, Subject } from 'rxjs';
import { SV_URL_AST, SV_URL_SOCKET } from '../../@core/utils/utils';

/**
 * Serviço de WebSocket utilizando Socket.IO.
 *
 * Mantém conexão singleton no servidor principal e expõe streams
 * separados para eventos de tarefa e mensagem.
 */
@Injectable({ providedIn: 'root' })
export class WebsocketService {

  private mainSocket: Socket | null = null;
  private astralSocket: Socket | null = null;
  private mainSubject: Subject<any> | null = null;
  private astralSubject: Subject<any> | null = null;

  private readonly taskSubject = new Subject<any>();
  private readonly messageSubject = new Subject<any>();

  /** Stream de eventos `task` do servidor principal. */
  readonly taskEvents$ = this.taskSubject.asObservable();

  /** Stream de eventos `message` do servidor principal. */
  readonly messageEvents$ = this.messageSubject.asObservable();

  /**
   * Retorna Subject compartilhado do servidor principal (idempotente).
   *
   * Compatível com consumidores legados (ChatService) que enviam e recebem
   * mensagens pelo mesmo Subject.
   *
   * @returns Subject bidirecional conectado ao socket principal
   */
  connect(): Subject<any> {
    if (!this.mainSubject) {
      this.mainSocket = io(SV_URL_SOCKET, { transports: ['websocket'] });
      this.mainSubject = new Subject<any>();

      this.mainSocket.on('message', (data) => {
        this.messageSubject.next(data);
        this.mainSubject!.next(data);
      });

      this.mainSocket.on('task', (data) => {
        this.taskSubject.next(data);
        this.mainSubject!.next(data);
      });
    }
    return this.mainSubject;
  }

  /**
   * Envia payload pelo socket principal.
   *
   * @param data - Objeto a serializar e emitir como mensagem
   */
  emitMessage(data: object): void {
    if (!this.mainSocket) {
      this.connect();
    }
    this.mainSocket!.emit('message', JSON.stringify(data));
  }

  /**
   * Conecta ao servidor WebSocket do módulo Astral (conexão separada).
   *
   * @returns Subject bidirecional do socket Astral
   */
  connectAST(): Subject<any> {
    if (!this.astralSubject) {
      this.astralSocket = io(SV_URL_AST);
      this.astralSubject = new Subject<any>();

      this.astralSocket.on('message', (data) => {
        data['origin'] = 'message';
        this.astralSubject!.next(data);
      });
      this.astralSocket.on('newphoto', (data) => {
        data['origin'] = 'newphoto';
        this.astralSubject!.next(data);
      });
      this.astralSocket.on('newchange', (data) => {
        data['origin'] = 'newchange';
        this.astralSubject!.next(data);
      });
    }
    return this.astralSubject;
  }

  /**
   * Desconecta sockets ativos (ex.: logout).
   */
  disconnectAll(): void {
    this.mainSocket?.disconnect();
    this.astralSocket?.disconnect();
    this.mainSocket = null;
    this.astralSocket = null;
    this.mainSubject = null;
    this.astralSubject = null;
  }
}
